/**
 * Looping Jitter/Lottie in a sticky viewport; vertical scroll pans horizontally.
 * Desktop (≥1200px) only — mobile uses card rail.
 */

import lottie from 'lottie-web/build/player/lottie_light';

const RESIZE_DEBOUNCE_MS = 150;
const SCROLL_IDLE_MS = 150;
const DESKTOP_MIN_WIDTH = 1200;
const MASCOT_SLIDE_PX = 80;
/** Panorama distance (in design units) over which a mascot fades in / out. */
const MASCOT_FADE_PX = 300;

/**
 * The browser resolves an incoming #hash scroll before this script (loaded in
 * the footer) has expanded the panorama section to its scroll-driven height,
 * so any anchor below it lands short. Re-run the fragment scroll once the
 * real height is in place. Guarded to fire only once per page load, not on
 * every desktop/mobile re-init from resizing across DESKTOP_MIN_WIDTH.
 */
let hasReconciledInitialHash = false;

function reconcileInitialHashScroll() {
	if (hasReconciledInitialHash) {
		return;
	}
	hasReconciledInitialHash = true;

	if (!window.location.hash) {
		return;
	}

	const target = document.getElementById(window.location.hash.slice(1));
	target?.scrollIntoView({ block: 'start' });
}

/** Full comp visible, left-aligned; exact fit since layer aspect matches comp. */
const lottieCoverSettings = {
	preserveAspectRatio: 'xMinYMin meet',
};

/**
 * @param {Object} [options]
 * @return {{ destroy: () => void }}
 */
export function initPanoramicScroll(options = {}) {
	const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
	let inner = { destroy() { } };

	function onMqChange() {
		inner.destroy();
		inner = mq.matches ? startPanoramicScroll(options) : { destroy() { } };
	}

	onMqChange();
	mq.addEventListener('change', onMqChange);

	return {
		destroy() {
			mq.removeEventListener('change', onMqChange);
			inner.destroy();
		},
	};
}

/**
 * @param {Object} [_options]
 */
function startPanoramicScroll(_options = {}) {
	const outer = document.getElementById('panoramicScrollDesktop');
	const container = document.getElementById('panoramicScrollAnimation');
	const lottieUrl = container?.getAttribute('data-lottie-src');

	if (!outer || !container || !lottieUrl) {
		return { destroy() { } };
	}

	const ac = new AbortController();
	const { signal } = ac;
	let resizeTimer = null;
	/** @type {number} */
	let scrollIdleTimer = 0;
	let isScrolling = false;
	let pendingLoopRestart = false;
	/** @type {import('lottie-web').AnimationItem | null} */
	let anim = null;
	let compWidth = 0;
	let compHeight = 0;
	let panoramaDesignWidth = Number(container.dataset.panoramaWidth) || 9348;
	/** @type {NodeListOf<HTMLElement>} */
	let mascotEls = /** @type {NodeListOf<HTMLElement>} */ (container.querySelectorAll('.panoramic-scroll-section__mascot'));

	function clamp(value, min, max) {
		return Math.min(Math.max(value, min), max);
	}

	function getCalculatedRenderedWidth() {
		if (!compHeight || !panoramaDesignWidth) {
			return 0;
		}

		return window.innerHeight * (panoramaDesignWidth / compHeight);
	}

	function getPanRange() {
		const renderedWidth = getCalculatedRenderedWidth();
		if (!renderedWidth) {
			return 0;
		}

		return Math.max(0, renderedWidth - window.innerWidth);
	}

	function getScrollProgress() {
		const rect = outer.getBoundingClientRect();
		const span = Math.max(0, outer.offsetHeight - window.innerHeight);

		if (span <= 0) {
			return 0;
		}

		const scrolled = Math.min(Math.max(-rect.top, 0), span);
		return scrolled / span;
	}

	function applyPan() {
		const panRange = getPanRange();
		const x = -getScrollProgress() * panRange;
		container.style.transform = `translate3d(${x}px, 0, 0)`;
		updateMascots();
	}

	/** Right edge of the viewport, expressed in panorama design units. */
	function getViewEndDesign() {
		const renderedWidth = getCalculatedRenderedWidth();
		const panRange = getPanRange();
		if (!renderedWidth || panRange <= 0 || !panoramaDesignWidth) {
			return 0;
		}

		const scale = renderedWidth / panoramaDesignWidth;
		return (getScrollProgress() * panRange + window.innerWidth) / scale;
	}

	function applyMascotVisual(el, opacity, appearFrom) {
		const offset = (1 - opacity) * MASCOT_SLIDE_PX;
		const slide = appearFrom === 'left' ? -offset : offset;

		el.style.opacity = String(opacity);
		el.style.setProperty('--mascot-slide', `${slide}px`);
	}

	function hideMascot(el, appearFrom) {
		el.style.opacity = '0';
		el.style.setProperty(
			'--mascot-slide',
			appearFrom === 'left' ? `-${MASCOT_SLIDE_PX}px` : `${MASCOT_SLIDE_PX}px`,
		);
	}

	function updateMascots() {
		if (!mascotEls.length) {
			return;
		}

		const viewEnd = getViewEndDesign();
		if (!viewEnd) {
			mascotEls.forEach((el) => hideMascot(el, el.dataset.appearFrom));
			return;
		}

		// Visibility is driven by the viewport's right edge (viewEnd) sweeping across
		// the panorama. A mascot fades in once `appearsAfter` scrolls into view and
		// fades out once `disappearsAfter` scrolls into view. With no `disappearsAfter`
		// it stays to the end. Because this is purely position-based, reverse scrolling
		// mirrors the behaviour automatically.
		mascotEls.forEach((el) => {
			const appearFrom = el.dataset.appearFrom;
			const appearsAfter = Number(el.dataset.appearsAfter);
			const disappearsAfterRaw = el.dataset.disappearsAfter;
			const hasDisappear = disappearsAfterRaw != null && disappearsAfterRaw !== '';

			const fadeIn = clamp((viewEnd - appearsAfter) / MASCOT_FADE_PX, 0, 1);
			const fadeOut = hasDisappear
				? 1 - clamp((viewEnd - Number(disappearsAfterRaw)) / MASCOT_FADE_PX, 0, 1)
				: 1;
			const opacity = fadeIn * fadeOut;

			if (opacity <= 0) {
				hideMascot(el, appearFrom);
			} else {
				applyMascotVisual(el, opacity, appearFrom);
			}
		});
	}

	function syncPlayback() {
		if (!anim || isScrolling) {
			return;
		}

		if (pendingLoopRestart) {
			pendingLoopRestart = false;
			anim.goToAndPlay(0, true);
			return;
		}

		anim.play();
	}

	function onScrollActive() {
		if (!isScrolling) {
			isScrolling = true;
			anim?.pause();
			container.style.willChange = 'transform';
		}

		clearTimeout(scrollIdleTimer);
		scrollIdleTimer = window.setTimeout(() => {
			isScrolling = false;
			syncPlayback();
			container.style.willChange = '';
		}, SCROLL_IDLE_MS);
	}

	function onScroll() {
		onScrollActive();
		applyPan();
	}

	function bindLoopRestart() {
		if (!anim) {
			return;
		}

		anim.addEventListener('complete', () => {
			pendingLoopRestart = true;
			if (!isScrolling) {
				syncPlayback();
			}
		});
	}

	function applyOuterHeight() {
		const panRange = getPanRange();
		outer.style.height = `${window.innerHeight + panRange}px`;
	}

	function applyMascotPositions() {
		const renderedWidth = getCalculatedRenderedWidth();
		if (!renderedWidth || !mascotEls.length || !panoramaDesignWidth) {
			return;
		}

		const scale = renderedWidth / panoramaDesignWidth;

		mascotEls.forEach((el) => {
			const positionFromLeft = Number(el.dataset.positionFromLeft);
			if (!Number.isFinite(positionFromLeft)) {
				return;
			}

			el.style.left = `${positionFromLeft * scale}px`;
		});
	}

	function applyLayerSize() {
		const renderedWidth = getCalculatedRenderedWidth();

		if (!renderedWidth) {
			return;
		}

		container.style.width = `${renderedWidth}px`;
		container.style.height = '100%';
		anim?.resize();
		applyMascotPositions();
	}

	function setup() {
		applyLayerSize();
		applyOuterHeight();
		applyPan();
	}

	function scheduleResize() {
		if (resizeTimer) {
			clearTimeout(resizeTimer);
		}
		resizeTimer = window.setTimeout(() => {
			resizeTimer = null;
			setup();
		}, RESIZE_DEBOUNCE_MS);
	}

	const fetchJson = async (/** @type {string} */ url) => {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(String(response.status));
		}
		return response.json();
	};

	const onReady = () => {
		bindLoopRestart();
		setup();
		syncPlayback();
		reconcileInitialHashScroll();
	};

	const load = async () => {
		try {
			const data = await fetchJson(lottieUrl);
			compWidth = data.w || 0;
			compHeight = data.h || 0;
			// The wide comp itself is the full panorama; use its width unless overridden.
			if (compWidth) {
				panoramaDesignWidth = Number(container.dataset.panoramaWidth) || compWidth;
			}
			applyLayerSize();

			anim = lottie.loadAnimation({
				container,
				renderer: 'svg',
				loop: false,
				autoplay: true,
				animationData: data,
				rendererSettings: lottieCoverSettings,
			});

			if (anim.isLoaded) {
				onReady();
			} else {
				anim.addEventListener('DOMLoaded', onReady);
			}
		} catch (err) {
			console.warn('[PanoramicScroll] Fetch failed', err);
			reconcileInitialHashScroll();
		}
	};

	load();

	window.addEventListener('scroll', onScroll, { passive: true, signal });
	window.addEventListener('resize', scheduleResize, { signal });

	return {
		destroy() {
			ac.abort();
			clearTimeout(scrollIdleTimer);
			isScrolling = false;
			pendingLoopRestart = false;
			if (resizeTimer) {
				clearTimeout(resizeTimer);
				resizeTimer = null;
			}
			outer.style.height = '';
			container.style.width = '';
			container.style.height = '';
			container.style.transform = '';
			container.style.willChange = '';
			mascotEls.forEach((el) => {
				el.style.opacity = '';
				el.style.left = '';
				el.style.removeProperty('--mascot-slide');
			});
			if (anim) {
				anim.destroy();
				anim = null;
			}
		},
	};
}
